// Service 10 — Wills, succession and estate administration in Thailand (incl. foreign estates).
// Category definitions only (client-bundle safe, no corpus import).

export type InheritanceFaqCategory =
  | "will-types-thailand"
  | "foreign-will-recognition"
  | "statutory-heirs"
  | "estate-administrator-court"
  | "foreigner-inherit-land"
  | "condo-inheritance"
  | "bank-shares-insurance"
  | "inheritance-gift-tax"
  | "cross-border-documents"
  | "disputes-limitation";

export interface InheritanceCategoryDef {
  slug: InheritanceFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const INHERITANCE_FAQ_CATEGORIES: InheritanceCategoryDef[] = [
  {
    slug: "will-types-thailand",
    titleTh: "พินัยกรรมตามกฎหมายไทย — 5 แบบ และเงื่อนไขความสมบูรณ์",
    titleEn: "Wills under Thai Law — the Five Forms and Validity Requirements",
    descTh:
      "แบบพินัยกรรมตามประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1656–1663 ได้แก่ แบบธรรมดา แบบเขียนเองทั้งฉบับ แบบเอกสารฝ่ายเมือง แบบเอกสารลับ และแบบทำด้วยวาจา รวมถึงคุณสมบัติพยาน ผู้ทำพินัยกรรมอายุไม่ต่ำกว่า 15 ปี และเหตุที่ทำให้พินัยกรรมเป็นโมฆะ",
    descEn:
      "The five statutory forms of will under Civil and Commercial Code sections 1656-1663: ordinary written will, holograph will, will made before the district officer (public document), secret document will and oral will, plus witness requirements, the minimum age of 15 and the grounds that void a will.",
    matchTh: /(พินัยกรรม|แบบธรรมดา|เขียนเองทั้งฉบับ|เอกสารฝ่ายเมือง|เอกสารลับ|ด้วยวาจา|พยาน|1656|1657|1658|1663|โมฆะ|15 ปี)/i,
    matchEn: /(will|holograph|district officer|public document|secret document|oral will|witness|1656|1657|1658|1663|void|age of 15)/i,
  },
  {
    slug: "foreign-will-recognition",
    titleTh: "พินัยกรรมต่างประเทศกับทรัพย์สินในไทย — ใช้ได้แค่ไหน",
    titleEn: "Foreign Wills and Thai Assets — How Far They Apply",
    descTh:
      "หลักกฎหมายขัดกันตาม พ.ร.บ.ว่าด้วยการขัดกันแห่งกฎหมาย พ.ศ. 2481 แบบของพินัยกรรมและกฎหมายที่ใช้บังคับกับสังหาริมทรัพย์-อสังหาริมทรัพย์ การใช้พินัยกรรมฉบับต่างประเทศต่อศาลไทย และเหตุผลที่ควรทำพินัยกรรมไทยแยกฉบับสำหรับทรัพย์สินในไทย",
    descEn:
      "Conflict-of-laws rules under the Act on Conflict of Laws B.E. 2481, the law governing form and succession for movable and immovable property, using a foreign will before a Thai court, and why a separate Thai will for Thai-situs assets is usually recommended.",
    matchTh: /(พินัยกรรมต่างประเทศ|ขัดกันแห่งกฎหมาย|2481|ฉบับต่างประเทศ|สองฉบับ|แยกฉบับ|กฎหมายที่ใช้บังคับ|ภูมิลำเนา|สัญชาติ)/i,
    matchEn: /(foreign will|conflict of laws|2481|separate will|two wills|governing law|domicile|nationality|situs)/i,
  },
  {
    slug: "statutory-heirs",
    titleTh: "ทายาทโดยธรรม 6 ลำดับ และส่วนแบ่งของคู่สมรส",
    titleEn: "The Six Classes of Statutory Heirs and the Spouse's Share",
    descTh:
      "ลำดับทายาทโดยธรรมตาม ป.พ.พ. มาตรา 1629 สิทธิของผู้สืบสันดาน บิดามารดา พี่น้อง ปู่ย่าตายาย ลุงป้าน้าอา และส่วนแบ่งของคู่สมรสตามมาตรา 1635 รวมถึงการแยกสินสมรสก่อนแบ่งมรดก และสิทธิของบุตรนอกสมรสที่บิดารับรอง",
    descEn:
      "The order of statutory heirs under Civil and Commercial Code section 1629, rights of descendants, parents, siblings, grandparents and uncles/aunts, the surviving spouse's share under section 1635, division of marital property before the estate, and legitimated children born out of wedlock.",
    matchTh: /(ทายาทโดยธรรม|1629|1635|คู่สมรส|ผู้สืบสันดาน|บุตรนอกสมรส|สินสมรส|ลำดับ|ส่วนแบ่ง|1627)/i,
    matchEn: /(statutory heir|1629|1635|spouse|descendant|out of wedlock|marital property|class of heir|share|1627)/i,
  },
  {
    slug: "estate-administrator-court",
    titleTh: "ผู้จัดการมรดก — การยื่นคำร้องต่อศาลและหน้าที่ตามกฎหมาย",
    titleEn: "Estate Administrator — Court Petition and Statutory Duties",
    descTh:
      "การตั้งผู้จัดการมรดกตาม ป.พ.พ. มาตรา 1711–1713 ศาลที่มีเขตอำนาจตามภูมิลำเนาของผู้ตาย เอกสารประกอบคำร้อง (ใบมรณบัตร ทะเบียนบ้าน บัญชีทรัพย์ หนังสือให้ความยินยอมของทายาท) หน้าที่จัดทำบัญชีทรัพย์สินตามมาตรา 1728–1729 และผลเมื่อทายาทคัดค้าน",
    descEn:
      "Appointment of an estate administrator under Civil and Commercial Code sections 1711-1713, the court with jurisdiction based on the deceased's domicile, supporting documents (death certificate, house registration, asset schedule, heirs' consents), the duty to prepare an inventory under sections 1728-1729, and what happens when an heir objects.",
    matchTh: /(ผู้จัดการมรดก|คำร้อง|ศาล|1711|1713|1728|1729|บัญชีทรัพย์|มรณบัตร|ให้ความยินยอม|คัดค้าน)/i,
    matchEn: /(administrator|petition|court|1711|1713|1728|1729|inventory|death certificate|consent|objection|probate)/i,
  },
  {
    slug: "foreigner-inherit-land",
    titleTh: "ชาวต่างชาติรับมรดกที่ดิน — ข้อจำกัดและทางออกที่ถูกกฎหมาย",
    titleEn: "Foreigners Inheriting Land — Restrictions and Lawful Options",
    descTh:
      "ประมวลกฎหมายที่ดิน มาตรา 93 และ 94 คนต่างด้าวที่รับมรดกในฐานะทายาทโดยธรรมยังต้องได้รับอนุญาตจากรัฐมนตรี และหากถือไว้โดยไม่ได้รับอนุญาตต้องจำหน่ายภายในกำหนดเวลา รวมถึงแนวปฏิบัติของสำนักงานที่ดินและทางเลือกอย่างการเช่าระยะยาวหรือสิทธิเก็บกิน",
    descEn:
      "Land Code sections 93 and 94: a foreign statutory heir still needs ministerial permission, and land held without permission must be disposed of within the statutory period. Includes Land Office practice and alternatives such as long leases or usufruct.",
    matchTh: /(ที่ดิน|มาตรา 93|มาตรา 94|คนต่างด้าว|จำหน่าย|รัฐมนตรี|สำนักงานที่ดิน|สิทธิเก็บกิน|เช่าระยะยาว)/i,
    matchEn: /(land|section 93|section 94|alien|foreigner|dispose|minister|land office|usufruct|long lease)/i,
  },
  {
    slug: "condo-inheritance",
    titleTh: "รับมรดกห้องชุด — โควตาต่างชาติและการโอนที่สำนักงานที่ดิน",
    titleEn: "Inheriting a Condominium — Foreign Quota and Land Office Transfer",
    descTh:
      "การรับมรดกห้องชุดตามพระราชบัญญัติอาคารชุด พ.ศ. 2522 มาตรา 19 ทวิ และ 19 เบญจ กรณีทายาทต่างชาติไม่เข้าเงื่อนไขถือกรรมสิทธิ์หรือโควตา 49% เต็ม ต้องจำหน่ายห้องชุดภายในหนึ่งปี ขั้นตอนโอนมรดกโดยผู้จัดการมรดกและเอกสารที่สำนักงานที่ดินเรียก",
    descEn:
      "Inheriting a condominium unit under the Condominium Act B.E. 2522 sections 19 bis and 19 quinque: where a foreign heir does not qualify or the 49% quota is full, the unit must be disposed of within one year. Also the administrator-led transfer procedure and Land Office document list.",
    matchTh: /(ห้องชุด|อาคารชุด|19 ทวิ|19 เบญจ|โควตา|49%|จำหน่ายภายในหนึ่งปี|โอนมรดก)/i,
    matchEn: /(condominium|condo unit|19 bis|19 quinque|quota|49%|dispose within one year|transfer of inheritance)/i,
  },
  {
    slug: "bank-shares-insurance",
    titleTh: "บัญชีธนาคาร หุ้น กองทุน ประกันชีวิต และรถยนต์",
    titleEn: "Bank Accounts, Shares, Funds, Life Insurance and Vehicles",
    descTh:
      "การปิดบัญชีและถอนเงินของผู้ตายที่ต้องใช้คำสั่งศาลตั้งผู้จัดการมรดกเกือบทุกกรณี การโอนหุ้นบริษัทจำกัดและหลักทรัพย์ในตลาด สิทธิของผู้รับประโยชน์ตามกรมธรรม์ที่ไม่ใช่ทรัพย์มรดกตาม ป.พ.พ. มาตรา 897 และการโอนทะเบียนรถกับกรมการขนส่งทางบก",
    descEn:
      "Closing the deceased's bank accounts (nearly always requires a court-appointed administrator), transferring private company shares and listed securities, the beneficiary's right under a life policy which is not part of the estate under section 897, and vehicle re-registration at the Department of Land Transport.",
    matchTh: /(บัญชีธนาคาร|ธนาคาร|หุ้น|กองทุน|หลักทรัพย์|ประกันชีวิต|ผู้รับประโยชน์|897|รถยนต์|ขนส่งทางบก)/i,
    matchEn: /(bank account|shares|fund|securities|life insurance|beneficiary|897|vehicle|land transport)/i,
  },
  {
    slug: "inheritance-gift-tax",
    titleTh: "ภาษีการรับมรดก ภาษีการให้ และค่าธรรมเนียมโอนมรดก",
    titleEn: "Inheritance Tax, Gift Tax and Estate Transfer Fees",
    descTh:
      "พระราชบัญญัติภาษีการรับมรดก พ.ศ. 2558 ยกเว้นมรดกส่วนที่ไม่เกิน 100 ล้านบาทต่อผู้ตายหนึ่งราย อัตรา 5% สำหรับบุพการี-ผู้สืบสันดาน และ 10% สำหรับผู้รับมรดกอื่น คู่สมรสได้รับยกเว้น การยื่นแบบ ภ.ม.60 ภายใน 150 วัน และค่าธรรมเนียมจดทะเบียนโอนมรดกที่กรมที่ดิน",
    descEn:
      "The Inheritance Tax Act B.E. 2558 exempts the first THB 100 million received from one deceased person, taxes ascendants/descendants at 5% and other heirs at 10%, exempts the spouse entirely, requires form Phor Mor 60 within 150 days, and the Land Department registration fee for inheritance transfers.",
    matchTh: /(ภาษีการรับมรดก|ภาษีการให้|100 ล้าน|5%|10%|ภ\.ม\.60|150 วัน|ค่าธรรมเนียม|2558|สรรพากร)/i,
    matchEn: /(inheritance tax|gift tax|100 million|5%|10%|Phor Mor 60|150 days|fee|2558|Revenue Department)/i,
  },
  {
    slug: "cross-border-documents",
    titleTh: "เอกสารข้ามประเทศ — Notary นิติกรณ์ Apostille และคำแปลรับรอง",
    titleEn: "Cross-Border Documents — Notarisation, Legalisation, Apostille and Certified Translation",
    descTh:
      "การใช้เอกสารต่างประเทศ เช่น Grant of Probate ใบมรณบัตร หนังสือมอบอำนาจ ในกระบวนการมรดกไทย ต้องผ่านการรับรองโดย Notary และสถานทูต/กระทรวงการต่างประเทศ แปลและรับรองคำแปลที่กรมการกงสุล และประเทศไทยจะเริ่มใช้อนุสัญญา Apostille วันที่ 28 กุมภาพันธ์ 2570",
    descEn:
      "Using foreign documents such as a Grant of Probate, death certificate or power of attorney in Thai estate proceedings: notarisation, embassy or MFA legalisation, certified translation at the Department of Consular Affairs, and Thailand's Apostille Convention entry into force on 28 February 2027.",
    matchTh: /(Notary|นิติกรณ์|Apostille|คำแปล|กรมการกงสุล|สถานทูต|Probate|มอบอำนาจ|2570)/i,
    matchEn: /(notar|legalis|legaliz|apostille|translation|consular affairs|embassy|probate|power of attorney|2027)/i,
  },
  {
    slug: "disputes-limitation",
    titleTh: "ข้อพิพาทมรดก อายุความ และการตัดทายาทโดยธรรม",
    titleEn: "Estate Disputes, Prescription Periods and Disinheritance",
    descTh:
      "อายุความมรดกตาม ป.พ.พ. มาตรา 1754 ทายาทต้องฟ้องภายใน 1 ปีนับแต่รู้หรือควรรู้ถึงการตาย และไม่เกิน 10 ปีนับแต่เจ้ามรดกตาย การตัดทายาทโดยธรรมตามมาตรา 1608–1609 การเพิกถอนพินัยกรรม การถูกกำจัดมิให้รับมรดก และแนวทางระงับข้อพิพาทก่อนขึ้นศาล",
    descEn:
      "Prescription under Civil and Commercial Code section 1754 (one year from knowledge of the death, and never more than ten years from death), disinheritance under sections 1608-1609, revocation of a will, exclusion from succession, and settling disputes before litigation.",
    matchTh: /(อายุความ|1754|1 ปี|10 ปี|ตัดทายาท|1608|1609|เพิกถอน|กำจัดมิให้รับมรดก|ข้อพิพาท|ฟ้อง)/i,
    matchEn: /(prescription|1754|one year|ten years|disinherit|1608|1609|revoke|exclusion|dispute|litigation)/i,
  },
];

export function getInheritanceCategoryDef(slug: string): InheritanceCategoryDef | undefined {
  return INHERITANCE_FAQ_CATEGORIES.find((c) => c.slug === slug);
}